import { Button, Card, CardContent, Label, Loader } from "@devhop/ui";
import { History } from "lucide-react";
import { useMemo, useState } from "react";
import type { ExchangeRateHistoryQueryDTO } from "@/modules/merchant/domain/contracts";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  exchangeRateKey,
  formatExchangeRateInputValue,
  SHOP_FX_EDIT_PAIRS,
} from "@/shared/lib/exchange-rate";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useExchangeRateHistoryQuery } from "../api/queries";

const PAGE_SIZE = 15;

/**
 * ສະຫຼຸບປະຫວັດການບັນທຶກອັດຕາ (ແຖວ shop — ບໍ່ລວມ global) ຈາກ GET …/exchange-rates/history
 */
export function ShopExchangeRateHistorySection({
  shopId,
}: {
  shopId: string | undefined;
}) {
  const [pairKey, setPairKey] = useState<string>("__all__");
  const [page, setPage] = useState(0);

  const queryParams = useMemo((): Partial<ExchangeRateHistoryQueryDTO> => {
    const o: Partial<ExchangeRateHistoryQueryDTO> = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (pairKey === "__all__") {
      return o;
    }
    const m = SHOP_FX_EDIT_PAIRS.find(
      (p) => exchangeRateKey(p.from, p.to) === pairKey,
    );
    if (m) {
      o.baseCurrency = m.from;
      o.quoteCurrency = m.to;
    }
    return o;
  }, [pairKey, page]);

  const history = useExchangeRateHistoryQuery(
    shopId,
    queryParams,
    Boolean(shopId),
  );

  const pairOptions = useMemo(
    () => [
      { value: "__all__", label: "ທຸກຄູ່ສະກຸນ" },
      ...SHOP_FX_EDIT_PAIRS.map(({ from, to }) => ({
        value: exchangeRateKey(from, to),
        label: `${from} → ${to}`,
      })),
    ],
    [],
  );

  const total = history.data?.meta.total ?? 0;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;
  const fromIdx = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const toIdx = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
      <CardContent className="space-y-4 pt-6 sm:space-y-5">
        <div className="flex gap-3 border-b border-border/60 pb-4">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-700 dark:text-violet-300">
            <History className="size-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="font-heading font-semibold text-base text-foreground sm:text-lg">
              ປະຫວັດອັດຕາ (ຮ້ານນີ້)
            </h2>
            <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
              ແຕ່ລະຄັ້ງທີ່ບັນທຶກເປັນແຖວໃໝ່ — ບໍ່ມີຂໍ້ມູນວ່າໃຄເປັນຜູ້ກົດບັນທຶກ. ລາຍການນີ້ບໍ່ລວມອັດຕາແພລດຟອມ (shop_id ເທົ່ານັ້ນ).
            </p>
          </div>
        </div>

        <div className="max-w-sm space-y-1.5">
          <Label>ກອງຄູ່ສະກຸນ</Label>
          <SimpleSelect
            value={pairKey}
            onValueChange={(v) => {
              setPairKey(v);
              setPage(0);
            }}
            options={pairOptions}
            placeholder="ເລືອກ"
            className="w-full"
          />
        </div>

        {history.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : history.isError ? (
          <p className="text-destructive text-sm">ໂຫຼດປະຫວັດບໍ່ສຳເລັດ</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-xl text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-3 py-2 font-medium">ຄູ່</th>
                    <th className="px-3 py-2 font-medium">ອັດຕາ</th>
                    <th className="px-3 py-2 font-medium">ມີຜົນແຕ່</th>
                    <th className="px-3 py-2 font-medium">ເວລາບັນທຶກ</th>
                    <th className="px-3 py-2 font-medium">ແຫຼ່ງ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(history.data?.items ?? []).map((r) => (
                    <tr key={r.id} className="hover:bg-muted/20">
                      <td className="px-3 py-2 font-mono text-xs">
                        {r.baseCurrency} → {r.quoteCurrency}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs tabular-nums">
                        {formatExchangeRateInputValue(r.rate)}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTimeLocal(r.effectiveFrom)}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTimeLocal(r.createdAt)}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total === 0 ? (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີປະຫວັດອັດຕາຂອງຮ້ານນີ້
              </p>
            ) : null}
            {total > 0 ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-muted-foreground text-xs">
                  ທັງໝົດ {total} ແຖວ — ສະແດງ {fromIdx}–{toIdx}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canPrev || history.isFetching}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    ກັບກ່ອນ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canNext || history.isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    ຕໍ່ໄປ
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
